import { defineStore } from 'pinia';

import AnalysisService from '@/Analysis/analysis.service';
import { Member } from '@/Member/member.entity';
import MemberService from '@/Member/member.service';
import { useAnalysisStore } from '@/stores/analysis';
import { useMemberStore } from '@/stores/member';

export type StagedStatus = 'ready' | 'uploading' | 'success' | 'error';

export type StagedFile = {
    id: number;
    file: File;
    status: StagedStatus;
};

export type UploadOutcome = {
    success: number;
    failed: number;
};

// Data layer for the "add decision" upload flow. Owns the assignable-analyst list, the staged
// file queue, and the batch upload itself, so AddDecisionModal is left with rendering and
// user intent only.
//
// The assignee list arrives from the backend already filtered, deduped and ordered (current
// user first, then analysts by name) — this store never re-sorts or re-filters it.
type UploadDecisionState = {
    assignableMembers: Member[];
    selectedAssigneeId: string | undefined;
    files: StagedFile[];
    submitting: boolean;
    // Every analysis created during this modal session, across retries. Fed to the
    // duplicate-decision check once the modal closes.
    createdBatch: { analysisId: string; fileName: string }[];
    nextId: number;
};

const initialState = (): UploadDecisionState => ({
    assignableMembers: [],
    selectedAssigneeId: undefined,
    files: [],
    submitting: false,
    createdBatch: [],
    nextId: 0,
});

export const useUploadDecisionStore = defineStore('uploadDecision', {
    state: initialState,
    getters: {
        hasReadyFiles(state: UploadDecisionState): boolean {
            return state.files.some((staged) => staged.status === 'ready');
        },
        hasFailed(state: UploadDecisionState): boolean {
            return state.files.some((staged) => staged.status === 'error');
        },
        // Once anything has uploaded successfully the assignee is fixed for the session:
        // changing it would silently apply to later files only.
        locked(state: UploadDecisionState): boolean {
            return state.submitting || state.files.some((staged) => staged.status === 'success');
        },
        hasCreated(state: UploadDecisionState): boolean {
            return state.createdBatch.length > 0;
        },
    },
    actions: {
        reset(): void {
            Object.assign(this, initialState());
        },

        async loadAssignableMembers(litigationId: string): Promise<void> {
            const currentMember = useMemberStore().member;

            this.selectedAssigneeId = currentMember?.memberId;
            this.assignableMembers = currentMember ? [currentMember] : [];

            this.assignableMembers = await new MemberService().findAssignable(litigationId);
        },

        selectAssignee(memberId: string | undefined): void {
            this.selectedAssigneeId = memberId;
        },

        // Stages the accepted files, skipping any already queued. Identity is
        // name + size + lastModified: these files have not been sent yet, so the server has no
        // way to spot the repeat. Server-side duplicate *decisions* are a separate check that
        // runs after upload (see similar-analyses.store).
        stageFiles(accepted: File[]): void {
            accepted.forEach((file) => {
                const duplicate = this.files.some(
                    (staged) =>
                        staged.file.name === file.name &&
                        staged.file.size === file.size &&
                        staged.file.lastModified === file.lastModified,
                );
                if (!duplicate) {
                    this.nextId += 1;
                    this.files.push({ id: this.nextId, file, status: 'ready' });
                }
            });
        },

        removeFile(id: number): void {
            this.files = this.files.filter((staged) => staged.id !== id);
        },

        requeueFailed(): void {
            this.files.forEach((staged) => {
                if (staged.status === 'error') staged.status = 'ready';
            });
        },

        // Uploads every ready file in parallel, each tracking its own status so one failure
        // never rejects the batch. Returns the tally for the caller to surface.
        async uploadReady(litigationName: string): Promise<UploadOutcome> {
            const ready = this.files.filter((staged) => staged.status === 'ready');

            if (ready.length === 0 || this.submitting) {
                return { success: 0, failed: 0 };
            }

            this.submitting = true;

            const analysisService = new AnalysisService();
            const analysisStore = useAnalysisStore();

            const outcomes = await Promise.all(
                ready.map(async (staged): Promise<boolean> => {
                    staged.status = 'uploading';
                    try {
                        const result = await analysisService.createAnalysis(
                            { file: staged.file, fileName: staged.file.name },
                            litigationName,
                            this.selectedAssigneeId,
                        );
                        staged.status = 'success';
                        if (result?.analysisId) {
                            analysisStore.markAsRecent(result.analysisId);
                            this.createdBatch.push({
                                analysisId: result.analysisId,
                                fileName: staged.file.name,
                            });
                        }
                        return true;
                    } catch {
                        staged.status = 'error';
                        return false;
                    }
                }),
            );

            this.submitting = false;

            const success = outcomes.filter(Boolean).length;

            return { success, failed: ready.length - success };
        },
    },
});
